import {
  // json,
  type LinksFunction,
  type LoaderFunction,
  type HeadersFunction,
  // redirect,
} from '@remix-run/node';
import {
  Links,
  // LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteLoaderData,
} from '@remix-run/react';
import { v4 as uuidv4 } from 'uuid';

import getConfig from '@/config';
import { getDefaultCookie } from '@/utils/cookies';

import { DialogProvider } from '@/contexts/DialogContext';
import { BreachCheckerContextProvider } from '@/contexts/BreachCheckerContext';

import { MemoizedOptimizely } from '@/components/optimizely/optimizely';
import {
  OneTrust,
  links as onetrustConsentStyles,
} from '@/components/onetrust-consent/onetrust-consent';
import { HeapScript } from '@/components/heap-script/heap-script';
import { GTMScript } from '@/components/google-tag-manager-script/google-tag-manager-script';
import { GoogleReCaptchaProviderComponent } from '@/components/google-re-captcha-provider/google-re-captcha-provider.component';
import {
  ErrorBoundaryTemplate,
  links as errorBoundaryTemplateLinks,
} from '@/components/error-boundary-template/error-boundary-template';
import { useScrollDepthTracker } from '@/hooks/useScrollDepthTracker';

import sharedStyles from '@/styles/shared.css?url';
import { getLandingType } from '@/utils/getLandingType';
import { LANDING_TYPE } from '@/types/landingTypes';
import { cspHeaderValue } from '@/utils/csp-headers';
import type { OptimizelyDecision } from '@optimizely/react-sdk';
import { createInstance } from '@optimizely/react-sdk';

export type MainLoaderData = {
  ENV: {
    RECAPTCHA_SITE_KEY: string;
    OPTIMIZELY_SDK_KEY: string;
    GTM_ID: string;
    NODE_ENV: 'development' | 'test' | 'production';
    ONE_TRUST_SCRIPTS_ID: string;
  };
  userId: string;
  isProd: boolean;
  isProveSandbox: boolean;
  landingType: LANDING_TYPE;
  decisions: Record<string, OptimizelyDecision>;
};

/*
headers parameters an object with the following keys:
{
  actionHeaders,
  errorHeaders,
  loaderHeaders,
  parentHeaders,
}
*/
export const headers: HeadersFunction = () => {
  return new Headers({
    'Content-Security-Policy': cspHeaderValue,
    'X-Frame-Options': 'DENY',
    'X-Content-Type-Options': 'nosniff',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
    'Permissions-Policy': 'geolocation=(), camera=(), microphone=()',
    'Strict-Transport-Security': 'max-age=31536000; includeSubDomains; preload',
  });
};

export const loader: LoaderFunction = async ({ request }) => {
  const config = await getConfig();

  const { pathname } = new URL(request.url);
  const landingType = getLandingType(pathname);

  let userId = getDefaultCookie('optimizelyEndUserId', request);

  const headers = new Headers();
  if (!userId) {
    userId = uuidv4();
    headers.append(
      'Set-Cookie',
      // set cookie for 1 year, we pay for each user in optimizely
      `optimizelyEndUserId=${userId}; Path=/; ${config.isDev ? '' : 'Domain=.aura.com;'} Max-Age=31104000; SameSite=Lax`,
    );
  }

  const optimizelyInstance = createInstance({
    sdkKey: config.OPTIMIZELY_SDK_KEY,
  });

  await optimizelyInstance.setUser({ id: userId });

  const mainLoaderData: MainLoaderData = {
    ENV: {
      RECAPTCHA_SITE_KEY: config.RECAPTCHA_SITE_KEY,
      OPTIMIZELY_SDK_KEY: config.OPTIMIZELY_SDK_KEY,
      GTM_ID: config.GTM_ID,
      NODE_ENV: config.NODE_ENV,
      ONE_TRUST_SCRIPTS_ID: config.ONE_TRUST_SCRIPTS_ID,
    },
    userId,
    isProd: config.isProd,
    isProveSandbox: config.PROVE_ENV === 'sandbox',
    landingType,
    decisions: {
      hp_tone: optimizelyInstance.decide('hp_tone'),
    },
  };
  return Response.json(mainLoaderData, { headers });
  // return json(mainLoaderData, { headers });
};

export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: sharedStyles },
  ...onetrustConsentStyles(),
  ...errorBoundaryTemplateLinks(),
];

export function ErrorBoundary() {
  return <ErrorBoundaryTemplate />;
}

export function Layout({ children }: { children: React.ReactNode }) {
  const data = useRouteLoaderData<MainLoaderData>('root');
  const { ENV, userId, landingType = LANDING_TYPE.HACKER_VIEW } = data || {};
  useScrollDepthTracker();

  const content = (() => {
    switch (landingType) {
      case LANDING_TYPE.SCAM_GAME: {
        return children;
      }
      case LANDING_TYPE.BREACH_CHECKER_EVERGREEN:
      case LANDING_TYPE.BREACH_SCAN:
      case LANDING_TYPE.BREACH_CHECKER_NATIONAL_PUBLIC_DATA: {
        return (
          <GoogleReCaptchaProviderComponent
            recaptchaSiteKey={ENV?.RECAPTCHA_SITE_KEY}
          >
            {/* we need `BreachCheckerContextProvider` here only cuz it lives in two places: ROOT_PATH + BREACH_CHECKER_EVERGREEN  */}
            <BreachCheckerContextProvider>
              {children}
            </BreachCheckerContextProvider>
          </GoogleReCaptchaProviderComponent>
        );
      }
      default: {
        return (
          <GoogleReCaptchaProviderComponent
            recaptchaSiteKey={ENV?.RECAPTCHA_SITE_KEY}
          >
            {children}
          </GoogleReCaptchaProviderComponent>
        );
      }
    }
  })();

  return (
    <html lang="en" data-type={landingType}>
      <head>
        <GTMScript env={ENV} />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.ico" type="image/x-icon" />
        <link
          rel="apple-touch-icon"
          sizes="256x256"
          href="/apple-touch-icon.png"
        />
        <Meta />
        <Links />
        <HeapScript userId={userId} env={ENV} />
        <OneTrust env={ENV} />
      </head>
      <body>
        <noscript data-env={ENV?.NODE_ENV}>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${ENV?.GTM_ID}`}
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
            title="GTM"
          />
        </noscript>

        <MemoizedOptimizely sdkKey={ENV?.OPTIMIZELY_SDK_KEY} userId={userId}>
          <DialogProvider>{content}</DialogProvider>
        </MemoizedOptimizely>
        <ScrollRestoration />
        <Scripts />
        <div id="dialog-root" />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}
